import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Filter, Plus, Trash2, TrendingDown, TrendingUp, RotateCcw, Eye, Upload, Users, ImageIcon, X } from 'lucide-react';
import RupiahIcon from '@/components/RupiahIcon';
import StaffTransactions from './StaffTransactions';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const resolveImg = (url) => !url ? '' : (url.startsWith('data:') || url.startsWith('http')) ? url : `${BACKEND_URL}${url}`;

const incomeCategories = [
  { value: 'rent_payment', label: 'Pembayaran Sewa' },
  { value: 'laundry', label: 'Pembayaran Laundry' },
  { value: 'deposit', label: 'Deposit Diterima' },
  { value: 'other_income', label: 'Pemasukan Lain' }
];

const expenseCategories = [
  { value: 'pengembalian_deposit', label: 'Pengembalian Deposit' },
  { value: 'gaji', label: 'Pembayaran Gaji' },
  { value: 'maintenance', label: 'Perbaikan/Maintenance' },
  { value: 'listrik', label: 'Listrik' },
  { value: 'air', label: 'Air' },
  { value: 'internet', label: 'Internet' },
  { value: 'cleaning', label: 'Pembayaran Vendor Laundry' },
  { value: 'iuran_keamanan', label: 'Iuran Keamanan' },
  { value: 'iuran_kebersihan', label: 'Iuran Kebersihan' },
  { value: 'pembelian', label: 'Pembelian Barang' },
  { value: 'other_expense', label: 'Pengeluaran Lain' }
];

const makePropertyKey = (property) => `${property.name}|||${property.owner_email}`;

const TransactionManagement = ({ userRole }) => {
  const [properties, setProperties] = useState([]);
  const [selectedPropertyKey, setSelectedPropertyKey] = useState('');
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [subTab, setSubTab] = useState('list');
  const [receiptFile, setReceiptFile] = useState(null);
  const [tenantInfo, setTenantInfo] = useState(null);
  const [extraFees, setExtraFees] = useState({ parkir: 0, laundry: 0, cleaning: 0, lainnya: 0, lainnya_desc: '' });
  const [rooms, setRooms] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tableLoading, setTableLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [filterStartDate, setFilterStartDate] = useState('');
  const [filterEndDate, setFilterEndDate] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterRoom, setFilterRoom] = useState('');
  const [receiptModal, setReceiptModal] = useState(null);
  const [formData, setFormData] = useState({
    transaction_date: new Date().toISOString().split('T')[0],
    transaction_type: 'income',
    category: 'rent_payment',
    room_number: '',
    room_type: '',
    description: '',
    amount: '',
    payment_method: 'cash'
  });
  const requiresSuperadmin = userRole !== 'superadmin' && formData.category === 'rent_payment' && !!formData.room_number;

  useEffect(() => {
    fetchProperties();
  }, []);

  useEffect(() => {
    if (!selectedPropertyKey) {
      setSelectedProperty(null);
      setTransactions([]);
      setRooms([]);
      setSummary(null);
      return;
    }

    const property = properties.find((item) => makePropertyKey(item) === selectedPropertyKey) || null;
    setSelectedProperty(property);

    if (property) {
      fetchPropertyData(property);
    }
  }, [selectedPropertyKey, properties]);

  const fetchProperties = async () => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const response = await axios.get(`${BACKEND_URL}/api/admin/properties`, config);
      setProperties(response.data);
      if (response.data.length > 0) {
        setSelectedPropertyKey(makePropertyKey(response.data[0]));
      }
    } catch (fetchError) {
      console.error('Error fetching properties:', fetchError);
      setError('Gagal memuat daftar properti');
    } finally {
      setLoading(false);
    }
  };

  const fetchPropertyData = async (property, filters = {}) => {
    try {
      setTableLoading(true);
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const params = new URLSearchParams({ owner_email: property.owner_email });

      if (filters.start_date) params.append('start_date', filters.start_date);
      if (filters.end_date) params.append('end_date', filters.end_date);
      if (filters.transaction_type && filters.transaction_type !== 'all') params.append('transaction_type', filters.transaction_type);
      if (filters.category && filters.category !== 'all') params.append('category', filters.category);
      if (filters.room_number && filters.room_number !== 'all') params.append('room_number', filters.room_number);

      const [transactionsRes, roomsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/admin/finance/${encodeURIComponent(property.name)}?${params.toString()}`, config),
        axios.get(`${BACKEND_URL}/api/admin/rooms/${encodeURIComponent(property.name)}?owner_email=${encodeURIComponent(property.owner_email)}`, config),
      ]);

      const txList = transactionsRes.data;
      const totalIncome  = txList.filter(t => t.transaction_type === 'income' && !['deposit', 'deposit_settled'].includes(t.category)).reduce((s, t) => s + (Number(t.amount) || 0), 0);
      const totalExpense = txList.filter(t => t.transaction_type === 'expense' && t.category !== 'pengembalian_deposit').reduce((s, t) => s + (Number(t.amount) || 0), 0);
      const depositIn = txList.filter(t => t.category === 'deposit').reduce((s, t) => s + (Number(t.amount) || 0), 0);
      const totalDeposit = depositIn;

      setTransactions(txList);
      setRooms(roomsRes.data);
      setSummary({ total_income: totalIncome, total_expense: totalExpense, net_income: totalIncome - totalExpense, total_deposit: totalDeposit });
    } catch (fetchError) {
      console.error('Error fetching transactions:', fetchError);
      setError('Gagal memuat data transaksi');
    } finally {
      setTableLoading(false);
    }
  };

  const handleRoomChange = async (roomNumber) => {
    if (!roomNumber || roomNumber === 'none') {
      setFormData((prev) => ({ ...prev, room_number: '', room_type: '', amount: '' }));
      setTenantInfo(null);
      setExtraFees({ parkir: 0, laundry: 0, cleaning: 0, lainnya: 0, lainnya_desc: '' });
      return;
    }
    const room = rooms.find((item) => item.room_number === roomNumber);
    setFormData((prev) => ({ ...prev, room_number: roomNumber, room_type: room?.room_type || '' }));
    // Jika kategori rent_payment, ambil data tenant & biaya
    if (formData.category === 'rent_payment' && selectedProperty) {
      try {
        const config = { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } };
        const res = await axios.get(`${BACKEND_URL}/api/admin/room-status/${encodeURIComponent(selectedProperty.name)}?owner_email=${encodeURIComponent(selectedProperty.owner_email)}`, config);
        const roomData = res.data.find(r => r.room_number === roomNumber);
        if (roomData?.tenant) {
          const t = roomData.tenant;
          setTenantInfo(t);
          setFormData(prev => ({ ...prev, amount: t.room_price || '', room_number: roomNumber, room_type: room?.room_type || '' }));
          setExtraFees({
            parkir: t.parkir ? (parseFloat(t.parkir_price) || 0) : 0,
            laundry: t.laundry ? (parseFloat(t.laundry_price) || 0) : 0,
            cleaning: t.cleaning ? (parseFloat(t.cleaning_price) || 0) : 0,
            lainnya: 0, lainnya_desc: '',
          });
        } else {
          setTenantInfo(null);
          setExtraFees({ parkir: 0, laundry: 0, cleaning: 0, lainnya: 0, lainnya_desc: '' });
        }
      } catch { setTenantInfo(null); }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (requiresSuperadmin) {
      setError('Pembayaran sewa kamar yang melunasi tagihan hanya dapat dicatat oleh Super Admin.');
      return;
    }
    setError('');
    setSuccess('');

    if (!selectedProperty) {
      setError('Pilih properti terlebih dahulu');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const totalAmount = formData.category === 'rent_payment'
        ? (parseFloat(formData.amount)||0) + (extraFees.parkir||0) + (extraFees.laundry||0) + (extraFees.cleaning||0) + (extraFees.lainnya||0)
        : parseFloat(formData.amount);
      const extraDesc = formData.category === 'rent_payment' ? [
        formData.description,
        extraFees.parkir > 0 ? `Parkir: Rp${extraFees.parkir.toLocaleString('id-ID')}` : '',
        extraFees.laundry > 0 ? `Laundry: Rp${extraFees.laundry.toLocaleString('id-ID')}` : '',
        extraFees.cleaning > 0 ? `Cleaning: Rp${extraFees.cleaning.toLocaleString('id-ID')}` : '',
        extraFees.lainnya > 0 && extraFees.lainnya_desc ? `${extraFees.lainnya_desc}: Rp${extraFees.lainnya.toLocaleString('id-ID')}` : '',
      ].filter(Boolean).join(' | ') : formData.description;
      const fd = new FormData();
      Object.keys(formData).forEach(k => { if (k !== 'amount' && k !== 'description') fd.append(k, formData[k] ?? ''); });
      fd.append('property_name', selectedProperty.name);
      fd.append('amount', totalAmount);
      fd.append('description', extraDesc || '');
      if (receiptFile && (formData.transaction_type === 'income' || formData.category === 'pengembalian_deposit')) fd.append('receipt', receiptFile);
      await axios.post(
        `${BACKEND_URL}/api/admin/finance?owner_email=${encodeURIComponent(selectedProperty.owner_email)}`,
        fd,
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } }
      );

      setSuccess('Transaksi berhasil ditambahkan');
      setReceiptFile(null);
      setTenantInfo(null);
      setExtraFees({ parkir: 0, laundry: 0, cleaning: 0, lainnya: 0, lainnya_desc: '' });
      setFormData({
        transaction_date: new Date().toISOString().split('T')[0],
        transaction_type: 'income',
        category: 'rent_payment',
        room_number: '',
        room_type: '',
        description: '',
        amount: '',
        payment_method: 'cash'
      });
      fetchPropertyData(selectedProperty, {
        start_date: filterStartDate,
        end_date: filterEndDate,
        transaction_type: filterType,
        category: filterCategory,
        room_number: filterRoom
      });
      setTimeout(() => setSuccess(''), 3000);
    } catch (submitError) {
      setError(submitError.response?.data?.detail || 'Gagal menambahkan transaksi');
    }
  };

  const handleFilter = () => {
    if (!selectedProperty) return;
    fetchPropertyData(selectedProperty, {
      start_date: filterStartDate,
      end_date: filterEndDate,
      transaction_type: filterType,
      category: filterCategory,
      room_number: filterRoom
    });
  };

  const handleShowAll = () => {
    if (!selectedProperty) return;
    setFilterStartDate('');
    setFilterEndDate('');
    setFilterType('');
    setFilterCategory('');
    setFilterRoom('');
    fetchPropertyData(selectedProperty, {});
  };

  const hasActiveFilter = filterStartDate || filterEndDate || filterType || filterCategory || filterRoom;

  const handleDelete = async (transaction) => {
    if (!selectedProperty || !window.confirm('Hapus transaksi ini?')) return;

    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      await axios.delete(
        `${BACKEND_URL}/api/admin/finance/${transaction.transaction_date}/${encodeURIComponent(transaction.property_name)}/${transaction.amount}?owner_email=${encodeURIComponent(selectedProperty.owner_email)}`,
        config
      );
      setSuccess('Transaksi berhasil dihapus');
      fetchPropertyData(selectedProperty, {
        start_date: filterStartDate,
        end_date: filterEndDate,
        transaction_type: filterType,
        category: filterCategory,
        room_number: filterRoom
      });
      setTimeout(() => setSuccess(''), 3000);
    } catch (deleteError) {
      setError(deleteError.response?.data?.detail || 'Gagal menghapus transaksi');
    }
  };

  const formatCurrency = (amount) =>
    new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0
    }).format(amount);

  const formatDate = (dateString) =>
    new Date(dateString).toLocaleDateString('id-ID', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });

  const downloadCSV = () => {
    if (!transactions.length) return;
    const headers = ['Tanggal', 'Jenis', 'Kategori', 'Room', 'Deskripsi', 'Nominal', 'Metode'];
    const rows = transactions.map(t => [
      t.transaction_date,
      t.transaction_type === 'income' ? 'Pemasukan' : 'Pengeluaran',
      getCategoryLabel(t.category, t.transaction_type),
      t.room_number || '-',
      `"${(t.description || '').replace(/"/g, '""')}"`,
      t.amount,
      t.payment_method || '-',
    ]);
    const csv = [headers, ...rows].map(r => r.join(',')).join('\n');
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const dateStr = new Date().toISOString().slice(0, 10);
    a.href = url;
    a.download = `transaksi_${selectedProperty?.name?.replace(/\s+/g, '_')}_${dateStr}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const printReport = () => {
    if (!selectedProperty) return;

    const now = new Date();
    const printDate = now.toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' });
    const printTime = now.toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit' });

    const dateRange = (filterStartDate || filterEndDate)
      ? `${filterStartDate ? new Date(filterStartDate).toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' }) : '...'} s/d ${filterEndDate ? new Date(filterEndDate).toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' }) : '...'}`
      : 'Semua Periode';

    // Calculate totals from current transactions (filtered)
    const totalIncome  = transactions.filter(t => t.transaction_type === 'income').reduce((s, t) => s + parseFloat(t.amount), 0);
    const totalExpense = transactions.filter(t => t.transaction_type === 'expense').reduce((s, t) => s + parseFloat(t.amount), 0);
    const netProfit    = totalIncome - totalExpense;
    const isProfit     = netProfit >= 0;

    // Group by category for breakdown
    const incomeByCategory  = {};
    const expenseByCategory = {};
    transactions.forEach(t => {
      const label = getCategoryLabel(t.category, t.transaction_type);
      if (t.transaction_type === 'income') {
        incomeByCategory[label]  = (incomeByCategory[label]  || 0) + parseFloat(t.amount);
      } else {
        expenseByCategory[label] = (expenseByCategory[label] || 0) + parseFloat(t.amount);
      }
    });

    const breakdownRows = (obj, colorClass) =>
      Object.entries(obj).map(([label, amt]) => `
        <tr>
          <td style="padding:5px 10px;border-bottom:1px solid #f0f0f0;font-size:11px;">${label}</td>
          <td style="padding:5px 10px;border-bottom:1px solid #f0f0f0;font-size:11px;text-align:right;" class="${colorClass}">${formatCurrency(amt)}</td>
        </tr>`).join('');

    let no = 0;
    const detailRows = transactions.map(t => {
      no++;
      return `<tr>
        <td>${no}</td>
        <td>${new Date(t.transaction_date).toLocaleDateString('id-ID', { day: '2-digit', month: 'short', year: 'numeric' })}</td>
        <td><span class="badge-${t.transaction_type}">${t.transaction_type === 'income' ? 'Pemasukan' : 'Pengeluaran'}</span></td>
        <td>${getCategoryLabel(t.category, t.transaction_type)}</td>
        <td>${t.room_number || '-'}</td>
        <td>${t.description || '-'}</td>
        <td style="text-align:right" class="${t.transaction_type === 'income' ? 'income' : 'expense'}">${formatCurrency(t.amount)}</td>
      </tr>`;
    }).join('');

    const html = `<!DOCTYPE html>
<html lang="id">
<head>
<meta charset="UTF-8">
<title>Laporan Keuangan – ${selectedProperty.name}</title>
<style>
  * { margin:0; padding:0; box-sizing:border-box; }
  body { font-family:'Arial',sans-serif; font-size:12px; color:#1a1a1a; background:#fff; padding:36px 40px; }

  /* ── Kop Surat ── */
  .kop { display:flex; justify-content:space-between; align-items:center; padding-bottom:14px; border-bottom:3px solid #0B6F63; margin-bottom:6px; }
  .kop-left .brand { font-size:24px; font-weight:900; color:#0B6F63; letter-spacing:-0.5px; }
  .kop-left .tagline { font-size:10px; color:#555; margin-top:2px; }
  .kop-right { text-align:right; }
  .kop-right .doc-title { font-size:15px; font-weight:bold; color:#1a1a1a; text-transform:uppercase; letter-spacing:1px; }
  .kop-right .doc-sub { font-size:10px; color:#777; margin-top:3px; }
  .kop-divider { border:none; border-top:1px solid #d1e7e4; margin-bottom:18px; }

  /* ── Info Laporan ── */
  .info-grid { display:grid; grid-template-columns:1fr 1fr; gap:0; border:1px solid #d0e8e4; border-radius:6px; overflow:hidden; margin-bottom:20px; }
  .info-row { display:flex; border-bottom:1px solid #e8f0ef; }
  .info-row:last-child { border-bottom:none; }
  .info-label { width:140px; padding:6px 12px; background:#f0f7f6; font-size:10.5px; color:#555; font-weight:600; border-right:1px solid #d0e8e4; flex-shrink:0; }
  .info-value { padding:6px 12px; font-size:10.5px; color:#1a1a1a; }

  /* ── Ringkasan ── */
  .section-title { font-size:11px; font-weight:700; text-transform:uppercase; letter-spacing:1px; color:#0B6F63; margin-bottom:8px; padding-bottom:4px; border-bottom:1px solid #d1e7e4; }
  .summary-grid { display:grid; grid-template-columns:1fr 1fr; gap:16px; margin-bottom:20px; }
  .breakdown-table { width:100%; border-collapse:collapse; margin-bottom:20px; }
  .breakdown-table thead tr { background:#f0f7f6; }
  .breakdown-table th { padding:6px 10px; font-size:10.5px; font-weight:700; color:#0B6F63; text-align:left; border:1px solid #d0e8e4; }
  .breakdown-table td { border:1px solid #ececec; }
  .breakdown-total td { background:#f9fafb; font-weight:700; font-size:11px; border-top:2px solid #d0e8e4 !important; }

  /* ── Detail Transaksi ── */
  .detail-table { width:100%; border-collapse:collapse; font-size:10.5px; margin-bottom:20px; }
  .detail-table thead tr { background:#0B6F63; color:#fff; }
  .detail-table th { padding:7px 8px; font-weight:600; text-align:left; }
  .detail-table td { padding:6px 8px; border-bottom:1px solid #efefef; vertical-align:top; }
  .detail-table tbody tr:nth-child(even) td { background:#f9fafb; }
  .detail-table tfoot tr { background:#f0f7f6; font-weight:700; }
  .detail-table tfoot td { padding:7px 8px; border-top:2px solid #0B6F63; font-size:11px; }

  /* ── Profit Box ── */
  .profit-box { margin-top:8px; border-radius:8px; overflow:hidden; border:2px solid ${isProfit ? '#0B6F63' : '#dc2626'}; }
  .profit-header { background:${isProfit ? '#0B6F63' : '#dc2626'}; color:#fff; padding:10px 20px; display:flex; justify-content:space-between; align-items:center; }
  .profit-header .title { font-size:13px; font-weight:700; text-transform:uppercase; letter-spacing:1px; }
  .profit-header .status { font-size:11px; background:rgba(255,255,255,0.2); padding:2px 10px; border-radius:99px; }
  .profit-body { padding:14px 20px; background:${isProfit ? '#f0fdf4' : '#fef2f2'}; }
  .profit-calc { display:flex; flex-direction:column; gap:6px; }
  .profit-line { display:flex; justify-content:space-between; font-size:11.5px; }
  .profit-line.separator { border-top:1px dashed #ccc; padding-top:6px; margin-top:2px; }
  .profit-line.total { font-size:14px; font-weight:900; color:${isProfit ? '#0B6F63' : '#dc2626'}; border-top:2px solid ${isProfit ? '#0B6F63' : '#dc2626'}; padding-top:8px; margin-top:4px; }

  /* ── Tanda Tangan ── */
  .signature-section { display:grid; grid-template-columns:1fr 1fr; gap:40px; margin-top:32px; }
  .sign-box { text-align:center; }
  .sign-box .role { font-size:10.5px; font-weight:700; color:#1a1a1a; margin-bottom:48px; }
  .sign-box .sign-line { border-top:1px solid #333; padding-top:6px; font-size:10px; color:#555; }

  /* ── Footer ── */
  .doc-footer { margin-top:24px; padding-top:10px; border-top:1px solid #e0e0e0; display:flex; justify-content:space-between; font-size:9px; color:#aaa; }

  .income { color:#16a34a; font-weight:600; }
  .expense { color:#dc2626; font-weight:600; }
  .badge-income { background:#dcfce7; color:#15803d; padding:1px 7px; border-radius:99px; font-size:9.5px; font-weight:600; white-space:nowrap; }
  .badge-expense { background:#fee2e2; color:#b91c1c; padding:1px 7px; border-radius:99px; font-size:9.5px; font-weight:600; white-space:nowrap; }

  @media print {
    body { padding:20px 24px; }
    @page { margin:16mm; size:A4 portrait; }
  }
</style>
</head>
<body>

<!-- KOP SURAT -->
<div class="kop">
  <div class="kop-left">
    <div class="brand">ISK House</div>
    <div class="tagline">Premium Property Management</div>
  </div>
  <div class="kop-right">
    <div class="doc-title">Laporan Keuangan</div>
    <div class="doc-sub">Dicetak: ${printDate}, ${printTime}</div>
  </div>
</div>
<hr class="kop-divider">

<!-- INFO LAPORAN -->
<div class="info-grid">
  <div>
    <div class="info-row">
      <div class="info-label">Nama Properti</div>
      <div class="info-value"><strong>${selectedProperty.name}</strong></div>
    </div>
    <div class="info-row">
      <div class="info-label">Periode</div>
      <div class="info-value">${dateRange}</div>
    </div>
  </div>
  <div>
    <div class="info-row">
      <div class="info-label">Jumlah Transaksi</div>
      <div class="info-value">${transactions.length} transaksi</div>
    </div>
    <div class="info-row">
      <div class="info-label">Dibuat oleh</div>
      <div class="info-value">ISK House System</div>
    </div>
  </div>
</div>

<!-- RINGKASAN KATEGORI -->
<div class="summary-grid">
  <div>
    <div class="section-title">Rincian Pemasukan</div>
    <table class="breakdown-table">
      <thead><tr><th>Kategori</th><th style="text-align:right">Jumlah</th></tr></thead>
      <tbody>
        ${Object.keys(incomeByCategory).length > 0 ? breakdownRows(incomeByCategory, 'income') : '<tr><td colspan="2" style="padding:8px 10px;color:#aaa;font-size:11px;">Tidak ada data</td></tr>'}
        <tr class="breakdown-total">
          <td style="padding:6px 10px;">Total Pemasukan</td>
          <td style="padding:6px 10px;text-align:right;" class="income">${formatCurrency(totalIncome)}</td>
        </tr>
      </tbody>
    </table>
  </div>
  <div>
    <div class="section-title">Rincian Pengeluaran</div>
    <table class="breakdown-table">
      <thead><tr><th>Kategori</th><th style="text-align:right">Jumlah</th></tr></thead>
      <tbody>
        ${Object.keys(expenseByCategory).length > 0 ? breakdownRows(expenseByCategory, 'expense') : '<tr><td colspan="2" style="padding:8px 10px;color:#aaa;font-size:11px;">Tidak ada data</td></tr>'}
        <tr class="breakdown-total">
          <td style="padding:6px 10px;">Total Pengeluaran</td>
          <td style="padding:6px 10px;text-align:right;" class="expense">${formatCurrency(totalExpense)}</td>
        </tr>
      </tbody>
    </table>
  </div>
</div>

<!-- DETAIL TRANSAKSI -->
<div class="section-title">Detail Transaksi</div>
<table class="detail-table">
  <thead>
    <tr>
      <th style="width:28px">No</th>
      <th style="width:88px">Tanggal</th>
      <th style="width:80px">Jenis</th>
      <th style="width:110px">Kategori</th>
      <th style="width:50px">Room</th>
      <th>Deskripsi</th>
      <th style="width:110px;text-align:right">Nominal</th>
    </tr>
  </thead>
  <tbody>${detailRows}</tbody>
  <tfoot>
    <tr>
      <td colspan="6" style="text-align:right">Subtotal Pemasukan</td>
      <td style="text-align:right" class="income">${formatCurrency(totalIncome)}</td>
    </tr>
    <tr>
      <td colspan="6" style="text-align:right">Subtotal Pengeluaran</td>
      <td style="text-align:right" class="expense">${formatCurrency(totalExpense)}</td>
    </tr>
  </tfoot>
</table>

<!-- PROFIT / RUGI -->
<div class="profit-box">
  <div class="profit-header">
    <span class="title">Laporan Laba / Rugi</span>
    <span class="status">${isProfit ? 'LABA' : 'RUGI'}</span>
  </div>
  <div class="profit-body">
    <div class="profit-calc">
      <div class="profit-line">
        <span>Total Pendapatan (Pemasukan)</span>
        <span class="income">${formatCurrency(totalIncome)}</span>
      </div>
      <div class="profit-line">
        <span>Total Beban (Pengeluaran)</span>
        <span class="expense">(${formatCurrency(totalExpense)})</span>
      </div>
      <div class="profit-line separator"></div>
      <div class="profit-line total">
        <span>${isProfit ? 'LABA BERSIH' : 'RUGI BERSIH'}</span>
        <span>${isProfit ? '+' : '-'}${formatCurrency(Math.abs(netProfit))}</span>
      </div>
    </div>
  </div>
</div>

<!-- TANDA TANGAN -->
<div class="signature-section">
  <div class="sign-box">
    <div class="role">Dibuat Oleh</div>
    <div class="sign-line">( _________________________ )</div>
    <div style="font-size:10px;color:#555;margin-top:4px;">Admin / Pengelola</div>
  </div>
  <div class="sign-box">
    <div class="role">Diketahui Oleh</div>
    <div class="sign-line">( _________________________ )</div>
    <div style="font-size:10px;color:#555;margin-top:4px;">Pemilik / Owner</div>
  </div>
</div>

<!-- FOOTER -->
<div class="doc-footer">
  <span>ISK House Property Management System</span>
  <span>Dokumen ini dicetak secara otomatis pada ${printDate} pukul ${printTime}</span>
</div>

<script>window.onload = () => { window.print(); }</script>
</body>
</html>`;

    const w = window.open('', '_blank');
    w.document.write(html);
    w.document.close();
  };

  const getCategoryLabel = (category, type) => {
    if (category === 'rent_daily') return 'Pendapatan Sewa';
    if (category === 'deposit_settled') return 'Deposit Diselesaikan';
    const categories = type === 'income' ? incomeCategories : expenseCategories;
    return categories.find((item) => item.value === category)?.label || category;
  };

  if (loading) {
    return (
      <div className="py-12 text-center">
        <div className="mx-auto mb-4 h-12 w-12 animate-spin rounded-full border-4 border-isk-teal border-t-transparent" />
        <p className="text-isk-muted">Memuat data transaksi...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {success && <div className="rounded-xl border border-green-200 bg-green-50 p-4 text-green-700">{success}</div>}
      {error && <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-red-700">{error}</div>}

      <div className="glass-card rounded-2xl p-6">
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] lg:items-end">
          <div className="space-y-2">
            <Label className="font-semibold text-isk-text">Pilih Properti</Label>
            <Select value={selectedPropertyKey} onValueChange={setSelectedPropertyKey}>
              <SelectTrigger className="glass-input rounded-xl border-2 focus:border-isk-teal">
                <SelectValue placeholder="Pilih properti untuk input transaksi" />
              </SelectTrigger>
              <SelectContent>
                {properties.map((property) => (
                  <SelectItem key={makePropertyKey(property)} value={makePropertyKey(property)}>
                    {property.name} • {property.owner_email}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {selectedProperty && (
            <div className="rounded-xl border border-isk-teal/20 bg-isk-teal/5 p-4">
              <p className="text-sm font-semibold text-isk-text">{selectedProperty.name}</p>
              <p className="mt-1 text-sm text-isk-muted">{selectedProperty.location}</p>
              <p className="mt-1 text-xs text-isk-teal">Owner: {selectedProperty.owner_email}</p>
            </div>
          )}
        </div>
      </div>

      {selectedProperty && summary && (
        <div className="grid grid-cols-1 gap-6 md:grid-cols-4">
          <div className="glass-card rounded-2xl p-6">
            <div className="mb-4 flex items-center justify-between">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-green-500/10">
                <TrendingUp className="h-6 w-6 text-green-600" />
              </div>
            </div>
            <p className="mb-1 text-sm text-isk-muted">Total Pemasukan</p>
            <p className="text-2xl font-bold text-green-600">{formatCurrency(summary.total_income)}</p>
          </div>

          <div className="glass-card rounded-2xl p-6">
            <div className="mb-4 flex items-center justify-between">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-red-500/10">
                <TrendingDown className="h-6 w-6 text-red-600" />
              </div>
            </div>
            <p className="mb-1 text-sm text-isk-muted">Total Pengeluaran</p>
            <p className="text-2xl font-bold text-red-600">{formatCurrency(summary.total_expense)}</p>
          </div>

          <div className="glass-card rounded-2xl p-6">
            <div className="mb-4 flex items-center justify-between">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-isk-teal/10">
                <RupiahIcon className="h-6 w-6 text-isk-teal" />
              </div>
            </div>
            <p className="mb-1 text-sm text-isk-muted">Pendapatan Bersih</p>
            <p className="text-2xl font-bold text-isk-teal">{formatCurrency(summary.net_income)}</p>
          </div>

          <div className="glass-card rounded-2xl p-6">
            <div className="mb-4 flex items-center justify-between">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-isk-gold/10">
                <RupiahIcon className="h-6 w-6 text-isk-gold" />
              </div>
            </div>
            <p className="mb-1 text-sm text-isk-muted">Total Deposit</p>
            <p className="text-2xl font-bold text-isk-gold">{formatCurrency(summary.total_deposit || 0)}</p>
            <p className="text-xs text-isk-muted mt-1">Kewajiban dikembalikan</p>
          </div>
        </div>
      )}

      {/* Sub-tabs */}
      <div className="flex gap-1 bg-white/40 rounded-xl p-1 w-fit mb-4 flex-wrap">
        <button onClick={() => setSubTab('list')}
          className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-colors ${subTab === 'list' ? 'bg-white shadow text-isk-text' : 'text-isk-muted hover:text-isk-text'}`}>
          Daftar Transaksi
        </button>
        <button onClick={() => setSubTab('input')}
          className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-colors ${subTab === 'input' ? 'bg-white shadow text-isk-text' : 'text-isk-muted hover:text-isk-text'}`}>
          Input Transaksi
        </button>
        <button onClick={() => setSubTab('staff')}
          className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-colors flex items-center gap-1 ${subTab === 'staff' ? 'bg-white shadow text-isk-text' : 'text-isk-muted hover:text-isk-text'}`}>
          <Users className="w-3.5 h-3.5" /> Transaksi Staff
        </button>
      </div>

      {subTab === 'input' && (
      <div className="max-w-lg">
          <div className="glass-card rounded-2xl p-6">
            <h3 className="mb-6 flex items-center text-xl font-bold text-isk-text">
              <Plus className="mr-2 h-5 w-5" />
              Input Transaksi
            </h3>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="transaction-date">Tanggal *</Label>
                <Input
                  id="transaction-date"
                  type="date"
                  required
                  value={formData.transaction_date}
                  onChange={(e) => setFormData({ ...formData, transaction_date: e.target.value })}
                  className="glass-input rounded-xl border-2 focus:border-isk-teal"
                />
              </div>

              <div className="space-y-2">
                <Label>Jenis Transaksi *</Label>
                <Select
                  value={formData.transaction_type}
                  onValueChange={(value) =>
                    setFormData((prev) => ({
                      ...prev,
                      transaction_type: value,
                      category: value === 'income' ? 'rent_payment' : 'maintenance'
                    }))
                  }
                >
                  <SelectTrigger className="glass-input rounded-xl border-2 focus:border-isk-teal">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="income">Pemasukan</SelectItem>
                    <SelectItem value="expense">Pengeluaran</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Kategori *</Label>
                <Select value={formData.category} onValueChange={(value) => setFormData({ ...formData, category: value })}>
                  <SelectTrigger className="glass-input rounded-xl border-2 focus:border-isk-teal">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {(formData.transaction_type === 'income' ? incomeCategories : expenseCategories).map((category) => (
                      <SelectItem key={category.value} value={category.value}>
                        {category.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Room/Unit</Label>
                <Select value={formData.room_number || 'none'} onValueChange={handleRoomChange}>
                  <SelectTrigger className="glass-input rounded-xl border-2 focus:border-isk-teal">
                    <SelectValue placeholder="Pilih room atau transaksi umum" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Tidak ada room</SelectItem>
                    {rooms.map((room) => (
                      <SelectItem key={room.room_number} value={room.room_number}>
                        {room.room_number} - {room.room_type} ({room.status})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {formData.room_type && (
                <div className="rounded-xl border border-isk-teal/20 bg-isk-teal/5 p-3 text-sm text-isk-muted">
                  Room Type: <span className="font-semibold text-isk-teal">{formData.room_type}</span>
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="transaction-description">Deskripsi *</Label>
                <Textarea
                  id="transaction-description"
                  required
                  rows={3}
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Contoh: Pembayaran kamar A1 bulan April"
                  className="glass-input resize-none rounded-xl border-2 focus:border-isk-teal"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="transaction-amount">{formData.category === 'rent_payment' ? 'Harga Sewa (Rp) *' : 'Nominal (Rp) *'}</Label>
                {tenantInfo && formData.category === 'rent_payment' && (
                  <p className="text-xs text-isk-muted mb-1">Tenant: <span className="font-medium text-isk-text">{tenantInfo.tenant_name}</span></p>
                )}
                <Input
                  id="transaction-amount"
                  type="number"
                  min="0"
                  required
                  value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                  className="glass-input rounded-xl border-2 focus:border-isk-teal"
                  placeholder="2000000"
                />
              </div>

              {/* Extra fees untuk rent_payment */}
              {formData.category === 'rent_payment' && (
                <div className="space-y-2 p-3 rounded-xl bg-gray-50 border border-gray-200">
                  <p className="text-xs font-semibold text-isk-text">Biaya Tambahan</p>
                  {[
                    { key: 'parkir', label: 'Parkir', auto: tenantInfo?.parkir },
                    { key: 'laundry', label: 'Laundry', auto: tenantInfo?.laundry },
                    { key: 'cleaning', label: 'Cleaning', auto: tenantInfo?.cleaning },
                  ].map(({ key, label, auto }) => (
                    <div key={key} className="flex items-center gap-2">
                      <Label className="text-xs w-20 shrink-0">{label}{auto ? ' ✓' : ''}</Label>
                      <Input type="number" min="0" value={extraFees[key] || ''}
                        onChange={e => setExtraFees(f => ({ ...f, [key]: parseFloat(e.target.value)||0 }))}
                        placeholder="0" className="rounded-lg h-8 text-xs flex-1" />
                    </div>
                  ))}
                  <div className="flex items-center gap-2">
                    <Input type="text" value={extraFees.lainnya_desc || ''}
                      onChange={e => setExtraFees(f => ({ ...f, lainnya_desc: e.target.value }))}
                      placeholder="Nama biaya lain" className="rounded-lg h-8 text-xs flex-1" />
                    <Input type="number" min="0" value={extraFees.lainnya || ''}
                      onChange={e => setExtraFees(f => ({ ...f, lainnya: parseFloat(e.target.value)||0 }))}
                      placeholder="0" className="rounded-lg h-8 text-xs w-24" />
                  </div>
                  {((parseFloat(formData.amount)||0) + extraFees.parkir + extraFees.laundry + extraFees.cleaning + extraFees.lainnya) > 0 && (
                    <div className="flex justify-between text-xs font-semibold pt-1 border-t border-gray-300">
                      <span className="text-isk-muted">Total</span>
                      <span className="text-isk-teal">{new Intl.NumberFormat('id-ID',{style:'currency',currency:'IDR',minimumFractionDigits:0}).format((parseFloat(formData.amount)||0)+extraFees.parkir+extraFees.laundry+extraFees.cleaning+extraFees.lainnya)}</span>
                    </div>
                  )}
                </div>
              )}

              <div className="space-y-2">
                <Label>Metode Pembayaran</Label>
                <Select value={formData.payment_method} onValueChange={(value) => setFormData({ ...formData, payment_method: value })}>
                  <SelectTrigger className="glass-input rounded-xl border-2 focus:border-isk-teal">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cash">Cash</SelectItem>
                    <SelectItem value="transfer">Transfer</SelectItem>
                    <SelectItem value="other">Lainnya</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {(formData.transaction_type === 'income' || formData.category === 'pengembalian_deposit') && (
                <div className="space-y-2">
                  <Label>{formData.category === 'pengembalian_deposit' ? 'Bukti Pengembalian Deposit *' : 'Bukti Transfer / Foto'}</Label>
                  <label className={`flex flex-col items-center justify-center w-full h-24 border-2 border-dashed rounded-xl cursor-pointer transition-colors ${receiptFile ? 'border-isk-teal bg-isk-teal/5' : 'border-gray-300 hover:bg-gray-50'}`}>
                    <Upload className="w-6 h-6 mb-1 text-isk-muted" />
                    <span className={`text-xs ${receiptFile ? 'text-isk-teal font-medium' : 'text-isk-muted'}`}>
                      {receiptFile ? `✓ ${receiptFile.name}` : 'Klik untuk upload bukti (opsional)'}
                    </span>
                    <input type="file" accept="image/*,.pdf" className="hidden" onChange={e => setReceiptFile(e.target.files?.[0] || null)} />
                  </label>
                </div>
              )}

              {requiresSuperadmin && (
                <p className="text-sm text-yellow-700">Pembayaran sewa kamar yang melunasi tagihan hanya dapat dicatat oleh Super Admin.</p>
              )}
              <Button type="submit" disabled={requiresSuperadmin} className="w-full rounded-full bg-isk-teal py-6 font-semibold text-white hover:bg-isk-dark">
                Simpan Transaksi
              </Button>
            </form>
          </div>
      </div>
      )}

      {subTab === 'staff' && <StaffTransactions userRole={userRole} />}

      {subTab === 'list' && (
      <div>
          <div className="glass-card mb-6 rounded-2xl p-6">
            <h3 className="mb-4 flex items-center text-xl font-bold text-isk-text">
              <Filter className="mr-2 h-5 w-5" />
              Filter Transaksi
            </h3>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              <div className="space-y-2">
                <Label>Dari Tanggal</Label>
                <Input type="date" value={filterStartDate} onChange={(e) => setFilterStartDate(e.target.value)} className="glass-input rounded-xl border-2 focus:border-isk-teal" />
              </div>

              <div className="space-y-2">
                <Label>Sampai Tanggal</Label>
                <Input type="date" value={filterEndDate} onChange={(e) => setFilterEndDate(e.target.value)} className="glass-input rounded-xl border-2 focus:border-isk-teal" />
              </div>

              <div className="space-y-2">
                <Label>Jenis</Label>
                <Select value={filterType || 'all'} onValueChange={(value) => setFilterType(value === 'all' ? '' : value)}>
                  <SelectTrigger className="glass-input rounded-xl border-2 focus:border-isk-teal">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Semua</SelectItem>
                    <SelectItem value="income">Income</SelectItem>
                    <SelectItem value="expense">Expense</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Kategori</Label>
                <Select value={filterCategory || 'all'} onValueChange={(value) => setFilterCategory(value === 'all' ? '' : value)}>
                  <SelectTrigger className="glass-input rounded-xl border-2 focus:border-isk-teal">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Semua</SelectItem>
                    {[...incomeCategories, ...expenseCategories].map((category) => (
                      <SelectItem key={category.value} value={category.value}>
                        {category.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Room</Label>
                <Select value={filterRoom || 'all'} onValueChange={(value) => setFilterRoom(value === 'all' ? '' : value)}>
                  <SelectTrigger className="glass-input rounded-xl border-2 focus:border-isk-teal">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Semua</SelectItem>
                    {rooms.map((room) => (
                      <SelectItem key={room.room_number} value={room.room_number}>
                        {room.room_number}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-end gap-2">
                <Button onClick={handleFilter} className="flex-1 rounded-xl bg-isk-teal py-6 text-white hover:bg-isk-dark">
                  <Filter className="mr-2 h-4 w-4" /> Terapkan Filter
                </Button>
              </div>
            </div>

            <div className="mt-4 flex items-center justify-between gap-3 rounded-xl border border-isk-teal/20 bg-isk-teal/5 p-3">
              <p className="text-sm text-isk-muted">
                Menampilkan <span className="font-bold text-isk-teal">{transactions.length}</span> transaksi
                {hasActiveFilter && (
                  <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-yellow-100 px-2 py-0.5 text-xs font-medium text-yellow-700">
                    Filter aktif
                  </span>
                )}
              </p>
              <Button
                onClick={handleShowAll}
                disabled={!selectedProperty}
                variant="outline"
                size="sm"
                className="shrink-0 rounded-full border-isk-teal/40 text-isk-teal hover:bg-isk-teal hover:text-white text-xs"
              >
                <Eye className="mr-1.5 h-3.5 w-3.5" />
                Tampilkan Semua
              </Button>
            </div>
          </div>

          <div className="glass-card rounded-2xl p-6">
            <div className="mb-6 flex items-center justify-between gap-3 flex-wrap">
              <h3 className="text-xl font-bold text-isk-text">Riwayat Transaksi</h3>
              {transactions.length > 0 && (
                <div className="flex gap-2">
                  <Button
                    onClick={downloadCSV}
                    variant="outline"
                    size="sm"
                    className="rounded-full border-isk-teal/40 text-isk-teal hover:bg-isk-teal hover:text-white text-xs"
                  >
                    <svg className="mr-1.5 h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
                    Download CSV
                  </Button>
                  <Button
                    onClick={printReport}
                    size="sm"
                    className="rounded-full bg-isk-teal text-white hover:bg-isk-dark text-xs"
                  >
                    <svg className="mr-1.5 h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" /></svg>
                    Cetak / PDF
                  </Button>
                </div>
              )}
            </div>

            {tableLoading ? (
              <div className="py-10 text-center">
                <div className="mx-auto mb-4 h-10 w-10 animate-spin rounded-full border-4 border-isk-teal border-t-transparent" />
                <p className="text-isk-muted">Memuat transaksi...</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-white/20">
                      <th className="px-4 py-3 text-left text-sm font-semibold text-isk-text">Tanggal</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-isk-text">Jenis</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-isk-text">Kategori</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-isk-text">Room</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-isk-text">Deskripsi</th>
                      <th className="px-4 py-3 text-right text-sm font-semibold text-isk-text">Nominal</th>
                      <th className="px-4 py-3 text-center text-sm font-semibold text-isk-text">Aksi</th>
                    </tr>
                  </thead>
                  <tbody>
                    {transactions.length > 0 ? (
                      transactions.map((transaction, index) => (
                        <tr key={`${transaction.transaction_date}-${transaction.amount}-${index}`} className="border-b border-white/10 hover:bg-white/30">
                          <td className="px-4 py-3 text-sm text-isk-muted">{formatDate(transaction.transaction_date)}</td>
                          <td className="px-4 py-3">
                            <span className={`rounded-full px-3 py-1 text-xs font-semibold ${transaction.transaction_type === 'income' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                              {transaction.transaction_type === 'income' ? 'Income' : 'Expense'}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm text-isk-text">{getCategoryLabel(transaction.category, transaction.transaction_type)}</td>
                          <td className="px-4 py-3 text-sm text-isk-muted">{transaction.room_number || '-'}</td>
                          <td className="px-4 py-3 text-sm text-isk-muted">{transaction.description}</td>
                          <td className={`px-4 py-3 text-right text-sm font-bold ${transaction.transaction_type === 'income' ? 'text-green-600' : 'text-red-600'}`}>
                            {formatCurrency(transaction.amount)}
                          </td>
                          <td className="px-4 py-3 text-center">
                            <div className="flex items-center justify-center gap-1">
                              {transaction.receipt_url && (
                                <Button
                                  onClick={() => setReceiptModal(resolveImg(transaction.receipt_url))}
                                  variant="outline"
                                  size="sm"
                                  className="border-isk-teal/40 text-isk-teal hover:bg-isk-teal hover:text-white"
                                  title="Lihat bukti"
                                >
                                  <ImageIcon className="h-4 w-4" />
                                </Button>
                              )}
                              {userRole === 'superadmin' && (
                                <Button
                                  onClick={() => handleDelete(transaction)}
                                  variant="outline"
                                  size="sm"
                                  className="border-red-300 text-red-600 hover:bg-red-600 hover:text-white"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="7" className="py-8 text-center text-isk-muted">Belum ada transaksi</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
      </div>
      )}

      {/* Receipt Image Modal */}
      {receiptModal && (
        <div
          className="fixed inset-0 bg-black/70 z-[60] flex items-center justify-center p-4"
          onClick={() => setReceiptModal(null)}
        >
          <div className="relative max-w-3xl w-full" onClick={e => e.stopPropagation()}>
            <button
              onClick={() => setReceiptModal(null)}
              className="absolute -top-10 right-0 text-white hover:text-white/70"
            >
              <X className="w-8 h-8" />
            </button>
            <div className="bg-white rounded-2xl overflow-hidden">
              <div className="bg-gray-900 flex flex-col items-center justify-center p-4 min-h-[320px]">
                <img
                  src={receiptModal}
                  alt="Bukti transfer"
                  className="max-w-full max-h-[75vh] object-contain rounded-lg"
                />
              </div>
              <div className="p-3 flex justify-between items-center border-t">
                <p className="text-sm text-isk-muted">Bukti Pembayaran</p>
                <a href={receiptModal} target="_blank" rel="noreferrer"
                  className="text-xs text-isk-teal hover:underline flex items-center gap-1">
                  <Eye className="w-3 h-3" /> Buka tab baru
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TransactionManagement;
